import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import MomentPage from "./MomentPage";

export default function QuickMomentWrapper() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [forceOpen, setForceOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  // ✅ Safe currentUser
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const currentUserId = currentUser?.id;

  // -----------------------------
  // 🔁 Fetch active moment session
  // -----------------------------
  const checkActive = async () => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }

    try {
      const res = await api.get(`/connect/active/${currentUserId}`);
      const session = res.data?.activeSession ?? res.data?.session ?? null;

      setActiveSession(session);

      // ✅ NEW: if a session appears, ensure UI isn't stuck hidden
      if (session?.id) {
        setIsHidden(false);
      }

      const isSuppressed = sessionStorage.getItem("moment_suppressed") === "1";

      if (session?.id && !isSuppressed) {
        sessionStorage.setItem("active_moment_id", String(session.id));
      } else {
        sessionStorage.removeItem("active_moment_id");
      }
    } catch {
      setActiveSession(null);
    } finally {
      setLoading(false);
    }
  };

  // Initial check + event listeners
  useEffect(() => {
    checkActive();

    const handleAccepted = (e) => {
      const session = e.detail?.session;
      if (!session?.id) return;

      sessionStorage.removeItem(`moment_left_${session.id}`);
      sessionStorage.removeItem("moment_suppressed");
      sessionStorage.removeItem("just_logged_in");

      sessionStorage.setItem("active_moment_id", String(session.id));

      setIsHidden(false);
      setForceOpen(true);
      setActiveSession(session);
    };

    const handleSuppress = () => {
      sessionStorage.setItem("moment_suppressed", "1");
    };

    const handleLeft = (e) => {
      console.log("👋 MOMENT LEFT — HIDING MOMENT UI", e.detail);
      setIsHidden(true);
    };

    const handleQuickOpen = async () => {
      console.log("⚡ QUICK OPEN — UNHIDE MOMENT");

      sessionStorage.removeItem("moment_suppressed");

      setIsHidden(false);
      setForceOpen(true);

      if (!activeSession?.id) {
        await checkActive();
      }
    };

    const handleMomentEnded = () => {
      console.log("🧨 MOMENT ENDED — RESETTING WRAPPER STATE");

      setActiveSession(null);
      setIsHidden(false);
      setForceOpen(false);

      sessionStorage.removeItem("active_moment_id");
      sessionStorage.setItem("moment_suppressed", "1");
    };

    const handleGoHome = () => {
      navigate("/app", { replace: true });
    };

    window.addEventListener("moment-accepted", handleAccepted);
    window.addEventListener("moment-suppress", handleSuppress);
    window.addEventListener("moment-left", handleLeft);
    window.addEventListener("quick-open", handleQuickOpen);
    window.addEventListener("moment-ended", handleMomentEnded);
    window.addEventListener("go-home", handleGoHome);

    return () => {
      window.removeEventListener("moment-accepted", handleAccepted);
      window.removeEventListener("moment-suppress", handleSuppress);
      window.removeEventListener("moment-left", handleLeft);
      window.removeEventListener("quick-open", handleQuickOpen);
      window.removeEventListener("moment-ended", handleMomentEnded);
      window.removeEventListener("go-home", handleGoHome);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, navigate, activeSession?.id]);

  // ✅ NEW: Poll server so inviter auto-opens when receiver accepts
  useEffect(() => {
    if (!currentUserId) return;

    const interval = setInterval(() => {
      const isSuppressed = sessionStorage.getItem("moment_suppressed") === "1";
      const justLoggedIn = sessionStorage.getItem("just_logged_in") === "1";

      // If user intentionally closed it, don't auto-open.
      if (isSuppressed || justLoggedIn) return;

      checkActive();
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // -----------------------------
  // Visibility rules
  // -----------------------------
  const isSuppressed = sessionStorage.getItem("moment_suppressed") === "1";
  const justLoggedIn = sessionStorage.getItem("just_logged_in") === "1";

  const hasLeftThisSession =
    activeSession?.id &&
    sessionStorage.getItem(`moment_left_${activeSession.id}`) === "1";

  const shouldShowMoment =
    !!activeSession?.id && !isHidden && (!hasLeftThisSession || forceOpen);

  if (loading || !shouldShowMoment || isSuppressed || justLoggedIn) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        pointerEvents: "auto",
      }}
    >
      <MomentPage
        sessionId={activeSession.id}
        onMounted={() => {
          setForceOpen(false);
          setIsHidden(false);
          sessionStorage.removeItem(`moment_left_${activeSession.id}`);
        }}
      />
    </div>
  );
}
